import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState, } from "react";
import { usePreferences, } from "../context/PreferencesContext";
import { useConnectionMode, } from "../context/ConnectionModeContext";
import { getConnectionModeCopy, } from "../utils/connectionModeCopy";
import ConnectionModeAudienceSummary from "./ConnectionModeAudienceSummary";
import ConnectionModeAdminDiagnostics from "./ConnectionModeAdminDiagnostics";
function statusLabel(state, copy) {
    if (!state.ready ||
        state.loading) {
        return copy.checking;
    }
    if (state.pending) {
        return copy.switching;
    }
    if (state.effectiveMode ===
        "disconnected") {
        return copy.disconnected;
    }
    if (state.status ===
        "automatic_fallback" ||
        state.status ===
            "reconnecting") {
        return copy.reconnecting;
    }
    if (state.reason ===
        "local_capability_limited") {
        return copy.limited;
    }
    return state.requestedMode ===
        "local"
        ? copy.local
        : copy.online;
}
export default function ConnectionModePanel() {
    const { preferences, } = usePreferences();
    const copy = getConnectionModeCopy(preferences.language);
    const { state, setMode, refresh, } = useConnectionMode();
    const [localError, setLocalError,] = useState("");
    const localSelected = state.requestedMode ===
        "local";
    const displayStatus = useMemo(() => statusLabel(state, copy), [
        state,
        copy,
    ]);
    const toggleMode = async () => {
        setLocalError("");
        try {
            await setMode(localSelected
                ? "online"
                : "local");
        }
        catch (error) {
            setLocalError(String(error?.message ||
                copy.unavailable));
        }
    };
    const retry = async () => {
        setLocalError("");
        try {
            await refresh();
        }
        catch { }
    };
    const cardStyle = {
        width: "min(920px, calc(100% - 24px))",
        margin: "16px auto",
        padding: 18,
        borderRadius: 18,
        border: "1px solid rgba(89, 232, 255, 0.34)",
        background: "linear-gradient(145deg, rgba(2,12,22,0.96), rgba(4,18,28,0.92))",
        boxShadow: "0 0 24px rgba(0,220,255,0.10)",
        color: "#effcff",
    };
    const trackStyle = {
        position: "relative",
        width: 76,
        height: 36,
        flex: "0 0 auto",
        borderRadius: 999,
        border: localSelected
            ? "1px solid rgba(255,190,80,0.9)"
            : "1px solid rgba(70,236,255,0.9)",
        background: localSelected
            ? "rgba(255,156,46,0.20)"
            : "rgba(0,220,255,0.18)",
        boxShadow: localSelected
            ? "0 0 18px rgba(255,160,45,0.36)"
            : "0 0 18px rgba(0,230,255,0.40)",
        cursor: state.pending
            ? "wait"
            : "pointer",
        opacity: state.pending
            ? 0.66
            : 1,
    };
    const thumbStyle = {
        position: "absolute",
        width: 28,
        height: 28,
        top: 3,
        left: localSelected
            ? 43
            : 4,
        borderRadius: "50%",
        background: localSelected
            ? "#ffc263"
            : "#7df6ff",
        boxShadow: localSelected
            ? "0 0 15px rgba(255,190,80,0.88)"
            : "0 0 15px rgba(80,240,255,0.88)",
        transition: "left 180ms ease, background 180ms ease",
    };
    return (_jsxs("section", { style: cardStyle, "aria-label": copy.title, children: [_jsxs("div", { style: {
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 18,
                    flexWrap: "wrap",
                }, children: [_jsxs("div", { style: {
                            flex: "1 1 420px",
                        }, children: [_jsx("div", { style: {
                                    fontSize: 12,
                                    letterSpacing: "0.16em",
                                    textTransform: "uppercase",
                                    color: "#72f4ff",
                                    fontWeight: 800,
                                    marginBottom: 5,
                                }, children: "AVAAIO" }), _jsx("h2", { style: {
                                    margin: 0,
                                    fontSize: 22,
                                }, children: copy.title }), _jsx("p", { style: {
                                    margin: "7px 0 0 0",
                                    lineHeight: 1.55,
                                    opacity: 0.84,
                                    fontSize: 14,
                                }, children: copy.subtitle })] }), _jsxs("div", { style: {
                            display: "flex",
                            alignItems: "center",
                            gap: 12,
                        }, children: [_jsx("span", { style: {
                                    fontWeight: localSelected
                                        ? 500
                                        : 900,
                                    opacity: localSelected
                                        ? 0.65
                                        : 1,
                                }, children: copy.online }), _jsx("button", { type: "button", role: "switch", "aria-checked": localSelected, "aria-label": copy.title, disabled: state.pending, onClick: () => {
                                    void toggleMode();
                                }, style: trackStyle, children: _jsx("span", { "aria-hidden": "true", style: thumbStyle }) }), _jsx("span", { style: {
                                    fontWeight: localSelected
                                        ? 900
                                        : 500,
                                    opacity: localSelected
                                        ? 1
                                        : 0.65,
                                }, children: copy.local })] })] }), (state.error ||
                localError) && (_jsxs("div", { role: "alert", style: {
                    marginTop: 12,
                    padding: 11,
                    borderRadius: 12,
                    border: "1px solid rgba(255,105,105,0.35)",
                    background: "rgba(255,60,60,0.08)",
                    fontSize: 13,
                }, children: [_jsx("div", { children: localError ||
                            state.error ||
                            copy.authRequired }), _jsx("button", { type: "button", onClick: () => {
                            void retry();
                        }, style: {
                            marginTop: 9,
                            padding: "7px 12px",
                            borderRadius: 999,
                            border: "1px solid rgba(105,239,255,0.45)",
                            background: "rgba(0,220,255,0.09)",
                            color: "#dffcff",
                            cursor: "pointer",
                        }, children: copy.retry })] })), _jsx(ConnectionModeAudienceSummary, {}), _jsx(ConnectionModeAdminDiagnostics, {})] }));
}
